import { useConfirm } from "../components/ConfirmProvider";
import { useEffect, useRef, useState } from "react";

const MAX_AGE = 14 * 24 * 3600 * 1000;
export default function useRecoverableDraft(key, value, dirty, onRestore) {
  const confirm = useConfirm();
  const owner = sessionStorage.getItem("username") || "local";
  const storageKey = `facturalite:draft:${owner}:${key}`;
  const serialized = JSON.stringify(value);
  const [pending, setPending] = useState(null);
  const [storageError, setStorageError] = useState(false);
  const latest = useRef(null);
  const suppressed = useRef(null);
  latest.current = { storageKey, serialized, dirty };

  useEffect(() => {
    setPending(null);
    suppressed.current = null;
    try {
      const item = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (item && item.updated > Date.now() - MAX_AGE && item.value && typeof item.value === "object") setPending(item);
      else localStorage.removeItem(storageKey);
    } catch { setStorageError(true); }
  }, [storageKey]);

  // Une fermeture enregistre la dernière saisie, la frappe courante est temporisée.
  useEffect(() => {
    if (pending) return;
    const persist = () => {
      const current = latest.current;
      if (current.storageKey !== storageKey || !current.dirty || suppressed.current === `${storageKey}:${current.serialized}`) return;
      try { localStorage.setItem(storageKey, JSON.stringify({ updated: Date.now(), value: JSON.parse(current.serialized) })); }
      catch { setStorageError(true); }
    };
    window.addEventListener("pagehide", persist);
    return () => { window.removeEventListener("pagehide", persist); persist(); };
  }, [storageKey, pending]);

  useEffect(() => {
    if (pending || !dirty || suppressed.current === `${storageKey}:${serialized}`) return;
    const timer = setTimeout(() => {
      try { localStorage.setItem(storageKey, JSON.stringify({ updated: Date.now(), value: JSON.parse(serialized) })); }
      catch { setStorageError(true); }
    }, 350);
    return () => clearTimeout(timer);
  }, [storageKey, serialized, dirty, pending]);

  const clearDraft = () => {
    suppressed.current = `${storageKey}:${latest.current.serialized}`;
    latest.current.dirty = false;
    try { localStorage.removeItem(storageKey); } catch { setStorageError(true); }
    setPending(null);
  };
  const draftNotice = <>
    {pending && <div role="status" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm"><p>Une saisie du {new Date(pending.updated).toLocaleString("fr-FR", { timeZone: "Europe/Paris" })} est disponible sur cet appareil.</p><div className="mt-3 flex flex-wrap gap-2"><button type="button" className="btn-primary" onClick={() => { onRestore(pending.value); setPending(null); }}>Restaurer le brouillon</button><button type="button" className="btn-secondary" onClick={async () => { if (await confirm({ title: "Supprimer le brouillon ?", description: "La saisie récupérable sera retirée de cet appareil. Le formulaire ouvert restera disponible.", confirmLabel: "Supprimer le brouillon", danger: true })) clearDraft(); }}>Supprimer le brouillon</button></div></div>}
    {storageError && <p role="alert" className="mb-3 text-sm text-amber-800">La sauvegarde du brouillon sur cet appareil est indisponible. Gardez cet écran ouvert jusqu'à l'enregistrement.</p>}
    {dirty && !pending && !storageError && suppressed.current !== `${storageKey}:${serialized}` && <p className="mb-3 text-xs text-gray-500">Brouillon conservé sur cet appareil pendant 14 jours. Aucun document n'est émis.</p>}
  </>;
  return { clearDraft, draftNotice };
}
